/* eslint-disable indent */
import { html, css, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import {
    loadLtiAdvantageItemsIfNeeded,
    publishLtiAdvantageItemAsComposition,
    unpublishLtiAdvantageItemAsComposition,
    createLtiAdvantageItem,
} from '../../store/ltiAdvantage/ltiAdvantage.js';
import { deploymentOptionLabel } from '../../store/ltiAdvantage/deployments.js';
import { previewElearning } from '../../store/preview/preview.js';
import { navigate } from '../../store/route/route.js';
import { translate } from '../../helpers/translate.js';
import { renderTimestamp } from '../../helpers/controls.js';
import '@lit-labs/virtualizer';
import '@polymer/app-layout/app-layout.js';
import '@polymer/paper-input/paper-input.js';
import '@vaadin/context-menu/theme/material/vaadin-context-menu.js';
import '@vaadin/select/theme/material/vaadin-select.js';
import '@cwmr/paper-fab-speed-dial/paper-fab-speed-dial.js';
import '@sdb-develop/learning-frontend';
import '../shared/sdb-search-input.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-status-message.js';
import '../shared/sdb-dialog.js';
import './sdb-lti-advantage-deployments-dialog.js';

@customElement('sdb-lti-advantage-item-list')
class SdbLtiAdvantageItemList extends connect(store)(LitElement) {

    @state()
    accessor ltiAdvantageItems;

    @state()
    accessor total;

    @state()
    accessor search;

    @state()
    accessor isLoadingFirstTime;

    @state()
    accessor user;

    @state()
    accessor hasGlobalId = false;

    @state()
    accessor deployments = [];

    @state()
    accessor args;

    // the add dialog renders in the overlay, outside this shadow root
    get _deploymentsDialog() {
        return this.renderRoot.querySelector('#deployments_dialog');
    }

    get newName() {
        return document.getElementById('lti_advantage_new_name');
    }

    get newDeployment() {
        return document.getElementById('lti_advantage_new_deployment');
    }

    stateChanged(state) {
        this.ltiAdvantageItems = state.ltiAdvantage.list || [];
        this.total = state.ltiAdvantage.total;
        this.search = state.ltiAdvantage.search;
        this.isLoadingFirstTime = state.ltiAdvantage.status === 'loading' && state.ltiAdvantage.page === -1;
        this.user = state.auth.user;
        this.hasGlobalId = state.env.hasGlobalId;
        this.deployments = state.ltiAdvantageDeployments?.list || [];
    }

    static styles = [
        sharedStyles,
        css`
            :host {
                max-width: var(--tcg-content-max-width);
                width: 100%;
            }

            .right-nav-bar {
                display:flex;
                align-items: center;
                justify-content: space-between;
            }

            sdb-search-input {
                flex-grow: 1;
            }

            #manage_deployments_button,
            sdb-content-listitem sdb-content-iconbutton {
                margin-left: 10px;
            }

            paper-fab-speed-dial {
                --paper-fab-speed-dial-position: fixed;
                --paper-fab-speed-dial-background: var(--btn-primary, var(--sdb-button-primary));
                --paper-fab-speed-dial-keyboard-focus-background: var(--btn-primary, var(--sdb-button-primary));
                --text-primary-color: var(--sdb-button-primary-text, #fff);
                z-index: 1;
            }

            paper-fab-speed-dial sdb-content-speeddialbutton {
                margin-left: auto;
            }

            #lti_advantage_items_list {
                contain: none !important; /* lit-virtualizer hack */
            }

            sdb-content-listitem {
                width: 100%; /* lit-virtualizer screws this up */
            }



            .no-results {
                width: 100%;
                max-width: var(--tcg-content-max-width);
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            #no_results_img {
                width: 400px;
                height: 400px;
                background-color: var(--tcg-secondary-background-color);
            }
        `,
    ];

    render() {
        if (this.isLoadingFirstTime) {
            return html`<sdb-loading></sdb-loading>`;
        }

        return html`
            <sdb-layout-60-40>
                <sdb-nav-titlebar slot="left">
                    <span>${translate('headerLtiAdvantageItems')}</span>
                    <span ?hidden=${!this.total}>&nbsp;(${this.total})</span>
                </sdb-nav-titlebar>
                <div class="right-nav-bar" slot="right">
                    <sdb-search-input
                        placeholder=${translate('search')}
                        value=${this.search}
                        @search=${(e) => { store.dispatch(loadLtiAdvantageItemsIfNeeded({ search: e.detail.searchText, reload: true })); }}>
                    </sdb-search-input>
                    <sdb-content-button
                        id="manage_deployments_button"
                        label=${translate('suppliers')}
                        icon="fal fa-plug"
                        outlined
                        ?hidden=${this.hasGlobalId}
                        @click=${() => { this._deploymentsDialog.open(); }}>
                    </sdb-content-button>
                </div>
            </sdb-layout-60-40>
            <sdb-layout-100>
                ${!this.total
                    ? html`<sdb-status-message label=${translate('noLtiAdvantageItemsFound')}></sdb-status-message>`
                    : html`
                        <sdb-content-list separator>
                            <lit-virtualizer
                                id="lti_advantage_items_list"
                                .items=${this.ltiAdvantageItems}
                                .renderItem=${(ltiAdvantageItem, index) => this._renderLtiAdvantageItemListItem(ltiAdvantageItem, index)}
                                .scrollTarget=${document}>
                            </lit-virtualizer>
                        </sdb-content-list>
                    `
                }
                ${this.hasGlobalId ? '' : html`
                    <paper-fab-speed-dial id="add_speed_dial">
                        <sdb-content-speeddialbutton
                            id="add_supplier_button"
                            icon="fal fa-plug"
                            label=${translate('speedDialSupplier')}
                            @click=${() => this._speedDial(() => this._deploymentsDialog.open({ add: true }))}>
                        </sdb-content-speeddialbutton>
                        <sdb-content-speeddialbutton
                            id="add_module_button"
                            icon="fal fa-laptop"
                            label=${translate('speedDialElearningModule')}
                            @click=${() => this._speedDial(() => this._openDialog('add', null))}>
                        </sdb-content-speeddialbutton>
                    </paper-fab-speed-dial>
                `}
            </sdb-layout-100>

            <sdb-dialog
                type="confirm"
                id="publish_dialog"
                heading=${translate('publishHeadingLtiAdvantageItem')}
                body=${translate('publishBodyLtiAdvantageItem')}
                confirmCaption=${translate('publish')}
                @confirm=${(e) => { store.dispatch(publishLtiAdvantageItemAsComposition({ item: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="unpublish_dialog"
                heading=${translate('unpublishHeadingLtiAdvantageItem')}
                body=${translate('unpublishBodyLtiAdvantageItem')}
                confirmCaption=${translate('unpublish')}
                @confirm=${(e) => { store.dispatch(unpublishLtiAdvantageItemAsComposition({ item: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="form"
                id="add_dialog"
                heading=${translate('createHeadingLtiAdvantageItem')}
                confirmCaption=${translate('create')}
                @confirm=${() => { this._create(); }}
                .bodyRenderer=${() => this._renderAddDialogBody()}>
            </sdb-dialog>

            <sdb-lti-advantage-deployments-dialog id="deployments_dialog"></sdb-lti-advantage-deployments-dialog>
        `;
    }

    firstUpdated() {
        this.dispatchEvent(
            new CustomEvent('fullscreen', {
                bubbles: true,
                composed: true,
                detail: {
                    fullscreen: false,
                },
            }),
        );
    }

    _renderAddDialogBody() {
        return html`
            <div style="width: 500px;">
                <paper-input
                    id="lti_advantage_new_name"
                    transparent
                    label=${translate('name')}>
                </paper-input>
                <!-- renders in the dialog overlay, so layout is inline; the gear opens the connections dialog on top -->
                <div style="display: flex; align-items: flex-end;">
                    ${this.deployments.length === 0
                        ? html`<p style="flex: 1; margin: 8px 0;">${translate('noDeploymentsForPortal')}</p>`
                        : html`
                            <vaadin-select
                                id="lti_advantage_new_deployment"
                                transparent
                                style="flex: 1;"
                                label=${translate('deployment')}
                                value=${this.deployments[0].contextId}
                                .items=${this.deployments.map((d) => ({ value: d.contextId, label: deploymentOptionLabel(d, translate('deploymentNotConfigured')) }))}>
                            </vaadin-select>
                        `
                    }
                    <sdb-content-iconbutton
                        id="add_manage_deployments_button"
                        icon="fal fa-plug"
                        size="24"
                        style="margin-left: 10px; margin-bottom: 4px;"
                        title=${translate('manageDeployments')}
                        @click=${() => { this._deploymentsDialog.open(); }}>
                    </sdb-content-iconbutton>
                </div>
            </div>
        `;
    }

    _create() {
        const name = this.newName?.value?.trim();
        const contextId = this.newDeployment?.value;

        if (!name || !contextId) {
            return;
        }

        store.dispatch(createLtiAdvantageItem({ name, contextId }));
        if (this.newName) {
            this.newName.value = '';
        }
    }

    _renderLtiAdvantageItemListItem(ltiAdvantageItem, index) {
        store.dispatch(loadLtiAdvantageItemsIfNeeded({ index }));

        const menuItems = [
            {
                text: translate('publish'),
                action: () => this._openDialog('publish', ltiAdvantageItem),
                hidden: this.hasGlobalId,
                disabled: ltiAdvantageItem.elearningType === 0,
            },
            {
                text: translate('unpublish'),
                action: () => this._openDialog('unpublish', ltiAdvantageItem),
                hidden: this.hasGlobalId,
                disabled: ltiAdvantageItem.elearningType === 1,
            },
            {
                text: translate('preview'),
                action: () => store.dispatch(previewElearning({ item: ltiAdvantageItem })),
            },
        ].filter(item => !item.hidden);

        return html`
            <sdb-content-listitem
                icon="fal fa-square-o"
                .headline=${ltiAdvantageItem.title}
                .subtitle=${this._getSubtitle(ltiAdvantageItem)}
                @click=${()=> { store.dispatch(navigate(`ltiadvantageitem/${ltiAdvantageItem.targetId}`)); }}>

                <sdb-content-chipbar slot="chipbar">
                    <sdb-content-chip label="catalogus" ?hidden=${ltiAdvantageItem.elearningType !== 0}></sdb-content-chip>
                </sdb-content-chipbar>

                <vaadin-context-menu
                    slot="secondaryAction"
                    open-on="click"
                    .items=${menuItems}
                    @item-selected=${(e) => {
                        e.stopPropagation();
                        e.detail.value?.action?.();
                    }}>
                    <sdb-content-iconbutton icon="fal fa-ellipsis-vertical"></sdb-content-iconbutton>
                </vaadin-context-menu>

            </sdb-content-listitem>
        `;
    }

    _getSubtitle(ltiAdvantageItem) {
        var s = `${translate('lastModified')}: ${renderTimestamp(ltiAdvantageItem.lastModified)}`;
        if (ltiAdvantageItem.supplier && ltiAdvantageItem.supplier.supplierKey) {
            s += ` | ${ltiAdvantageItem.supplier.name}`;
        }
        if (ltiAdvantageItem.description) {
            s += ` | ${ltiAdvantageItem.description}`;
        }
        return s;
    }

    // closes the speed dial before the chosen action opens its dialog
    _speedDial(action) {
        this.renderRoot.querySelector('#add_speed_dial').close();
        action();
    }

    _openDialog(action, args) {
        this.renderRoot.querySelector(`#${action}_dialog`).open(args);
    }
}
